import React, { useCallback, useMemo, useState } from "react";

import Decimal from "decimal.js";

import { HookResult } from "@fartherfinance/frontend/api/Types";
import { useTheme } from "@fartherfinance/frontend/theme/ThemeProvider";

import PerformanceIndicatorArrow from "@src/sharedComponents/PerformanceIndicatorArrow/PerformanceIndicatorArrow";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";

import styles from "./HistoricalPerformance.module.css";

const timeFrameOptions = ["1Y", "3Y", "5Y", "10Y"] as const;
type TimeFrame = typeof timeFrameOptions[number];

/**
 * `null` represents that there is not return performance for this timeframe and a no data placeholder should be shown
 */

type HistoricPerformanceTableProps = HookResult<{
  data: {
    return1Year: Decimal | null;
    return3Year: Decimal | null;
    return5Year: Decimal | null;
    return10Year: Decimal | null;
  };
}>;

const HistoricPerformanceTable = (
  props: HistoricPerformanceTableProps
): JSX.Element => {
  const [selectedTF, setSelectedTF] = useState<TimeFrame>("1Y");

  const setClassName = (timeFrame: TimeFrame): string => {
    return `${styles.timeframe} ${
      selectedTF === timeFrame ? styles.selected : ""
    }`;
  };

  const performance: Decimal | null = useMemo(() => {
    if (props.isLoading || props.hasError) {
      return null;
    }

    switch (selectedTF) {
      case "1Y":
        return props.data.data.return1Year;
      case "3Y":
        return props.data.data.return3Year;
      case "5Y":
        return props.data.data.return5Year;
      case "10Y":
        return props.data.data.return10Year;
      default:
        return null;
    }
  }, [props, selectedTF]);

  const onPerformanceClick = useCallback(
    (tf: TimeFrame) => () => {
      if (props.isLoading || props.hasError) {
        return;
      }

      setSelectedTF(tf);
    },
    [props.hasError, props.isLoading]
  );

  const isDisabled = props.isLoading || props.hasError;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <p className={styles.headerText}>Historical Performance</p>

        <div className={styles.perfDiv}>
          {props.isLoading ? (
            <Skeleton width={100} className={styles.loadingFiller} />
          ) : props.hasError || performance === null ? (
            <div className={styles.flexDiv}>
              <p className={styles.noDataText}>Performance Unavailable</p>
            </div>
          ) : (
            <PerformanceText performance={performance} />
          )}
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.tabs}>
          {timeFrameOptions.map((tf) => (
            <div
              key={tf}
              className={setClassName(tf)}
              onClick={onPerformanceClick(tf)}
              style={{
                opacity: isDisabled ? 0.3 : 1,
                cursor: isDisabled ? "default" : "pointer",
              }}
            >
              {tf}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HistoricPerformanceTable;

type PerformanceTextProps = {
  performance: Decimal;
};

const PerformanceText = (props: PerformanceTextProps) => {
  const {
    color: { $iconPerformancePositive, $iconPerformanceNegative },
  } = useTheme();

  return (
    <>
      <div className={styles.perfTriangle}>
        <PerformanceIndicatorArrow
          status={
            props.performance.greaterThanOrEqualTo(0) ? "positive" : "negative"
          }
        />
      </div>

      <p
        className={styles.perfText}
        style={{
          color: props.performance.greaterThanOrEqualTo(0)
            ? $iconPerformancePositive
            : $iconPerformanceNegative,
        }}
      >
        {`${
          props.performance.greaterThanOrEqualTo(0) ? "+" : ""
        }${props.performance.mul(100).toFixed(2)}%`}
      </p>
    </>
  );
};
